import { useContext, useState } from "react";

import { analyticsService } from "../../services/analytics-service";
import { AuthContext } from "../../providers/auth-provider/AuthProvider";
import { PinInput } from "../../components/Input/Input";
import { Subtitle1, PageTitle } from "../../brand/typographies";
import agents from "../../api/agents";
import { StorSelfButton } from "../../components/buttons/StorSelfButton";
import useToast from "../../hooks/useToast";
import { Box } from "@mui/material";
import { Colors } from "../../brand/colors";
import { VerificationState } from "./SmsVerificationWrapper";

interface VerifyOtpProps {
  phoneNumberState: string | null;
  section: VerificationState;
  setSection: React.Dispatch<React.SetStateAction<VerificationState>>;
  allowUnauthenticated?: boolean;
  bypassText?: string;
}

export const VerifyOtp = ({
  phoneNumberState,
  setSection,
  allowUnauthenticated,
  bypassText,
}: VerifyOtpProps) => {
  const { setTenantAuth, setBypassNumber } = useContext(AuthContext);

  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState("");

  const { toast } = useToast();

  const onSubmit = async (e: any) => {
    e.preventDefault();

    if (code.length < 6) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await agents.OTP.verifyOTP(code);
      // const res = await agents.OTP.verifyOTP(code);
      console.log(res.data || res.success)
      if (res.data || res.success) {
        setSubmitting(false);

        analyticsService.trackEvent("Authenticated Number", { phoneNumber : phoneNumberState! });
        toast.success("You've been authenticated");
  
        setTenantAuth({ phoneNumber : phoneNumberState! });
      }

      setTimeout(() => {
        // this will update the auth context, which will cause multiple
        // updates, such as fetching the ledgers for the account in the account provider
        // TODO: ? might want to just call the refetch ledgers method directly here
      }, 400);
    } catch (error) {
      analyticsService.trackEvent("Authentication Failed", null);

      if (error instanceof Error) {
        toast.error(error.message || "Unable to validate the otp.");
      } else {
        toast.error("Unable to validate the otp.");
      }
    }
  };

  const bypassOtp = async () => {
    analyticsService.trackEvent("Bypass OTP clicked", null);
    const response = await agents.OTP.bypassOtp();

    if (response.data) {
      analyticsService.trackEvent("Bypass OTP success", {
        phoneNumber: response.data,
      });
      setBypassNumber(response.data);
    } else {
      analyticsService.trackEvent("Bypass OTP failed", null);
      toast.error(response.error || "Something went wrong...");
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        flexDirection={"column"}
      >
        <PageTitle>Verify Number</PageTitle>
        <Subtitle1 sx={{ textAlign: "center", marginBottom: "16px" }}>
          Please enter the verification code that we sent to your phone via SMS:
        </Subtitle1>
        <Subtitle1
          onClick={() => setSection(VerificationState.ENTER_PHONE_NUMBER)}
          underlined
          bold
          sx={{ color: Colors.red }}
        >
          {formatPhoneNumber(phoneNumberState ?? "")}
        </Subtitle1>
      </Box>

      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
        sx={{ marginY: "64px" }}
      >
        <PinInput
          isInputNum
          inputmode="numeric"
          pattern="[0-9]"
          autocomplete="one-time-code"
          numInputs={6}
          pin={code}
          setPin={setCode}
        />
        <Subtitle1
          onClick={() => setSection(VerificationState.RESEND_OTP)}
          brandColor
          underlined
          center
        >
          Resend code
        </Subtitle1>

        {allowUnauthenticated && bypassText && (
          <Subtitle1 onClick={bypassOtp} brandColor underlined center sx={{ cursor: "pointer" }}>
            {bypassText}
          </Subtitle1>
        )}
      </Box>
      <StorSelfButton
        disabled={code.length < 6}
        pending={submitting}
        type="submit"
      >
        Continue
      </StorSelfButton>
    </form>
  );
};

const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, "");

  const nationalPrefix = "+1 "; // You can make this dynamic if needed

  if (cleaned.length <= 3) {
    return nationalPrefix + cleaned;
  } else if (cleaned.length <= 6) {
    return `${nationalPrefix}(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    return `${nationalPrefix}(${cleaned.slice(0, 3)}) ${cleaned.slice(
      3,
      6
    )}-${cleaned.slice(6)}`;
  }
};
